import { Info, Monitor, Smartphone } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import React from "react";

type PlatformType = "apple" | "google" | "both";

export interface SummaryCardProps {
  title: string;
  value: number | string;
  loading?: boolean;
  format?: "number" | "currency" | "text" | "percentage";
  icon?: React.ComponentType<{ className?: string }>;
  color?: string;
  tooltip?: string;
  platform?: PlatformType;
  footnote?: string;
}

function PlatformIndicator({ platform }: { platform: PlatformType }) {
  if (platform === "both") return null;

  if (platform === "apple") {
    return (
      <div className="flex items-center gap-1">
        <Smartphone className="w-3 h-3 text-blue-400" />
        <span className="text-xs text-blue-400 font-medium">iOS</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Monitor className="w-3 h-3 text-emerald-400" />
      <span className="text-xs text-emerald-400 font-medium">Android</span>
    </div>
  );
}

export function PlatformBadge({ platform, label }: { platform: PlatformType; label?: string }) {
  if (platform === "both") return null;

  const isApple = platform === "apple";
  const icon = isApple ? (
    <Smartphone className="w-4 h-4 text-blue-400" />
  ) : (
    <Monitor className="w-4 h-4 text-emerald-400" />
  );

  const text = label ?? (isApple ? "Apenas iOS" : "Apenas Android");
  const baseColor = isApple
    ? "text-blue-400 bg-blue-500/10 border-blue-500/20"
    : "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border ${baseColor}`}>
      {icon}
      <span>{text}</span>
    </div>
  );
}

export function SummaryCard({
  title,
  value,
  loading,
  format = "number",
  icon: Icon,
  color = "bg-[#3B82F633]",
  tooltip,
  platform = "both",
  footnote,
}: SummaryCardProps) {
  const formatValue = () => {
    if (loading) return "-";

    if (format === "currency") {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 2,
      }).format(Number(value));
    }

    if (format === "percentage") {
      return `${Number(value).toFixed(1)}%`;
    }

    if (format === "text") {
      return String(value);
    }

    const num = Number(value);
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`;
    }

    if (num >= 1_000) {
      return `${(num / 1_000).toFixed(1)}K`;
    }

    return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(num);
  };

  const resolvedFootnote = !loading
    ? footnote ??
      (platform === "apple"
        ? "Somente iOS (Apple Analytics)"
        : platform === "google"
          ? "Somente Android (Google Play Console)"
          : undefined)
    : undefined;

  return (
    <div className="bg-[#232323] border-none rounded-lg">
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          {Icon && (
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
              <Icon className="w-5 h-5 text-white" />
            </span>
          )}
          <div className="flex items-center gap-2 flex-1">
            <span className="text-white/60 text-sm">{title}</span>
            <PlatformIndicator platform={platform} />
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-white/40 hover:text-white/60 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="bg-[#232323] border-white/20 text-white max-w-xs">
                  <p className="text-sm">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={`text-2xl font-bold text-white ${!loading && resolvedFootnote ? "mb-2" : "mb-4"} text-center`}>
          {loading ? <div className="h-8 bg-white/10 rounded animate-pulse" /> : formatValue()}
        </div>
        {!loading && resolvedFootnote && (
          <p className="text-xs text-white/60 text-center mb-4">{resolvedFootnote}</p>
        )}
        <div className={`w-full h-2 rounded m-auto ${color}`} />
      </div>
    </div>
  );
}

